import React from 'react';
import { Box, Typography, Grid, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { TrendingUp, AlertTriangle } from 'lucide-react';

interface FinancialItem {
  id: string;
  description: string;
  amount: number;
  type: 'profit' | 'risk';
}

interface ProfitRiskAnalysisProps {
  currentItems: FinancialItem[];
  futureItems: FinancialItem[];
}

const SectionContainer = styled(Box)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
}));

const FinancialItemContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(1),
  background: 'rgba(26, 26, 46, 0.4)',
  '&:last-child': {
    marginBottom: 0,
  },
}));

const TypeChip = styled(Chip)<{ itemType: 'profit' | 'risk' }>(({ theme, itemType }) => ({
  backgroundColor: itemType === 'profit' ? 'rgba(0, 204, 153, 0.1)' : 'rgba(255, 59, 48, 0.1)',
  color: itemType === 'profit' ? '#00CC99' : '#ff3b30',
  borderRadius: theme.spacing(0.75),
  fontWeight: 'bold',
  textTransform: 'capitalize',
}));

const AmountText = styled(Typography)<{ itemType: 'profit' | 'risk' }>(({ itemType }) => ({
  color: itemType === 'profit' ? '#00CC99' : '#ff3b30',
  fontWeight: 'bold',
}));

const formatCurrency = (amount: number): string => {
  const prefix = amount >= 0 ? '+' : '';
  return `${prefix}$${Math.abs(amount).toLocaleString()}`;
};

const ProfitRiskAnalysis: React.FC<ProfitRiskAnalysisProps> = ({ currentItems, futureItems }) => {
  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <TrendingUp size={20} color="#8A2BE2" style={{ marginRight: 8 }} />
        <Typography variant="h5" sx={{ color: '#fff' }}>
          Profit/Risk Analysis
        </Typography>
      </Box>

      <SectionContainer>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AlertTriangle size={16} color="#ff3b30" style={{ marginRight: 8 }} />
                <Typography variant="h6" sx={{ color: '#fff' }}>
                  Current Profit/Risk
                </Typography>
              </Box>

              {currentItems.map((item) => (
                <FinancialItemContainer key={item.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TypeChip label={item.type} itemType={item.type} size="small" />
                    <Typography
                      variant="body2"
                      sx={{ color: 'rgba(255, 255, 255, 0.7)', ml: 2 }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                  <AmountText variant="h6" itemType={item.type}>
                    {formatCurrency(item.amount)}
                  </AmountText>
                </FinancialItemContainer>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp size={16} color="#00CC99" style={{ marginRight: 8 }} />
                <Typography variant="h6" sx={{ color: '#fff' }}>
                  Future Profit/Risk
                </Typography>
              </Box>

              {futureItems.map((item) => (
                <FinancialItemContainer key={item.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TypeChip label={item.type} itemType={item.type} size="small" />
                    <Typography
                      variant="body2"
                      sx={{ color: 'rgba(255, 255, 255, 0.7)', ml: 2 }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                  <AmountText variant="h6" itemType={item.type}>
                    {formatCurrency(item.amount)}
                  </AmountText>
                </FinancialItemContainer>
              ))}
            </Box>
          </Grid>
        </Grid>
      </SectionContainer>
    </Box>
  );
};

export default ProfitRiskAnalysis; 